import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './authApi';

export const exchangeApi = createApi({
  reducerPath: 'exchangeApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Exchange', 'OrderExchange'],
  endpoints: (builder) => ({

    // GET /api/v1/pos/orders/:orderId/exchanges
    getOrderExchanges: builder.query({
      query: ({ orderId, page = 1, per_page = 50 } = {}) => ({
        url: `/orders/${orderId}/exchanges`,
        params: { page, per_page },
      }),
      providesTags: (result, error, { orderId }) => [{ type: 'OrderExchange', id: `ORDER_${orderId}` }],
    }),

    // GET /api/v1/pos/orders/:orderId/exchanges/:id
    getPosExchangeById: builder.query({
      query: ({ orderId, id }) => `/orders/${orderId}/exchanges/${id}`,
      providesTags: (result, error, { id }) => [{ type: 'Exchange', id }],
    }),

    // GET /api/v1/pos/exchanges (flat store-wide list)
    getStoreExchanges: builder.query({
      query: ({ status, order_id, date_from, date_to, page = 1, per_page = 25 } = {}) => ({
        url: '/exchanges',
        params: { status, order_id, date_from, date_to, page, per_page },
      }),
      providesTags: [{ type: 'Exchange', id: 'STORE_LIST' }],
    }),

    // POST /api/v1/pos/orders/:orderId/exchanges/preview_new_items → live promotion
    // preview for the "New Items" side while the exchange cart is still being
    // built (no Exchange record exists yet). Nothing is persisted server-side —
    // safe to call on every cart change. Not tagged/invalidated: it's a pure
    // read-only preview, not a resource.
    previewExchangeNewItems: builder.mutation({
      query: ({ orderId, new_items }) => ({
        url: `/orders/${orderId}/exchanges/preview_new_items`,
        method: 'POST',
        body: { new_items },
      }),
    }),

    // POST /api/v1/pos/orders/:orderId/exchanges  → creates draft
    createExchange: builder.mutation({
      query: ({ orderId, return_items, new_items, refund_method }) => ({
        url: `/orders/${orderId}/exchanges`,
        method: 'POST',
        body: {
          exchange: {
            ...(refund_method ? { refund_method } : {}),
            return_items,
            new_items,
          },
        },
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'OrderExchange', id: `ORDER_${orderId}` },
      ],
    }),

    // PATCH /api/v1/pos/orders/:orderId/exchanges/:id  → update draft (full replacement)
    updateExchange: builder.mutation({
      query: ({ orderId, id, return_items, new_items, refund_method }) => ({
        url: `/orders/${orderId}/exchanges/${id}`,
        method: 'PATCH',
        body: {
          exchange: {
            ...(refund_method ? { refund_method } : {}),
            return_items,
            new_items,
          },
        },
      }),
      invalidatesTags: (result, error, { id, orderId }) => [
        { type: 'Exchange', id },
        { type: 'OrderExchange', id: `ORDER_${orderId}` },
      ],
    }),

    // POST /api/v1/pos/orders/:orderId/exchanges/:id/complete
    completeExchange: builder.mutation({
      query: ({ orderId, id, payments, refund_method }) => ({
        url: `/orders/${orderId}/exchanges/${id}/complete`,
        method: 'POST',
        body: {
          payments: payments || [],
          ...(refund_method ? { refund_method } : {}),
        },
      }),
      invalidatesTags: (result, error, { id, orderId }) => [
        { type: 'Exchange', id },
        { type: 'OrderExchange', id: `ORDER_${orderId}` },
        { type: 'Exchange', id: 'STORE_LIST' },
      ],
    }),

    // POST /api/v1/pos/orders/:orderId/exchanges/:id/cancel
    cancelExchange: builder.mutation({
      query: ({ orderId, id }) => ({
        url: `/orders/${orderId}/exchanges/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { id, orderId }) => [
        { type: 'Exchange', id },
        { type: 'OrderExchange', id: `ORDER_${orderId}` },
      ],
    }),
  }),
});

export const {
  useGetOrderExchangesQuery,
  useGetPosExchangeByIdQuery,
  useGetStoreExchangesQuery,
  usePreviewExchangeNewItemsMutation,
  useCreateExchangeMutation,
  useUpdateExchangeMutation,
  useCompleteExchangeMutation,
  useCancelExchangeMutation,
} = exchangeApi;
